import { z } from 'zod';
import { TenantPlanSchema } from './tenants';

/**
 * Billing & usage shared contract (ARCHITECTURE_PLAN §9 — `GET /billing`,
 * `POST /billing/checkout`, `POST /billing/portal`; §4.2 / §7.3 / §8 for the
 * data shapes). Single source of truth shared by the Worker (producers) and
 * the SPA (typed client) per §3.
 *
 * The billing surface is owner-only (§6.3) and reports subscription state
 * mirrored from the authoritative Stripe subscription (§7.3 — Stripe is the
 * source of truth; D1 mirrors it) plus the current-period usage meters vs the
 * matching `plan_limits` row.
 */

/** Kinds metered against the plan (migration 0004 `usage_counters.kind`). */
export const UsageKindSchema = z.enum(['voice_minutes', 'sms_segments', 'ai_units']);
export type UsageKind = z.infer<typeof UsageKindSchema>;

/** Plan allowance reference data (migration 0004 `plan_limits`). */
export const PlanLimitsSchema = z.object({
  plan: TenantPlanSchema,
  monthlyPriceCents: z.number().int(),
  voiceMinutes: z.number().int(),
  smsSegments: z.number().int(),
  seats: z.number().int(),
  overageVoiceCentsPerMin: z.number().int(),
  overageSmsCentsPerSeg: z.number().int(),
});
export type PlanLimits = z.infer<typeof PlanLimitsSchema>;

/**
 * Stripe subscription status as mirrored into `billing_accounts`
 * (§7.3). Mirrors Stripe's lifecycle states; `null` while the tenant has never
 * had a Stripe subscription (e.g. fresh trial).
 */
export const SubscriptionStatusSchema = z
  .enum([
    'trialing',
    'active',
    'past_due',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'unpaid',
    'paused',
  ])
  .nullable();
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

/** A single current-period usage counter (migration 0004 `usage_counters`). */
export const UsageCounterSchema = z.object({
  kind: UsageKindSchema,
  period: z.string(),
  quantity: z.number().int(),
});
export type UsageCounter = z.infer<typeof UsageCounterSchema>;

/** `GET /billing` response. */
export const BillingResponseSchema = z.object({
  plan: TenantPlanSchema,
  subscriptionStatus: SubscriptionStatusSchema,
  currentPeriodEnd: z.number().int().nullable(),
  planLimits: PlanLimitsSchema,
  /** Current-period usage counters; one entry per `UsageKind` actually used. */
  usage: z.array(UsageCounterSchema),
});
export type BillingResponse = z.infer<typeof BillingResponseSchema>;

/** `POST /billing/checkout` body — target plan to upgrade to. */
export const CheckoutBodySchema = z.object({
  plan: z.enum(['solo', 'business', 'growth']),
});
export type CheckoutBody = z.infer<typeof CheckoutBodySchema>;

/** `POST /billing/checkout` response — Stripe-hosted URL to redirect to. */
export const CheckoutResponseSchema = z.object({
  url: z.string().url(),
});
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;

/** `POST /billing/portal` response — Stripe-hosted portal URL. */
export const PortalResponseSchema = z.object({
  url: z.string().url(),
});
export type PortalResponse = z.infer<typeof PortalResponseSchema>;
